import { useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { ApiClient, Projekt, ItSchrank, ItAbgang } from '../api';
import { IconElektro, IconWarnung, IconSuche, IconHerunterladen } from '../Icons';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

interface Rahmen { api: ApiClient; projekt: Projekt | null }

/**
 * IT-Verteiler — der eigene Abschnitt.
 *
 * --- Warum eine eigene Seite ------------------------------------------------
 *
 * Auftrag Fachplaner 18.09.2026: „da die it verteiler mit sv und uv gemischt
 * sind sollte die basis IT verteiler einen eigenen abschnitt bekommen und aus
 * der sv und uv ansicht rausgelöst werden."
 *
 * Ein LAN-Verteiler mit Netzwerkdosen ist keine Unterverteilung. Er trägt kein
 * einziges Betriebsmittel aus der Verteilerbestückung (gemessen 18.09.2026:
 * 0 Zeilen an allen 26), sondern Patchfelder, Switches, Rangierfelder, PDU und
 * USV — und seine Grenze ist nicht der Strom, sondern die Höheneinheit.
 *
 * --- Muster: Baum links, Inhalt rechts (wie Heizung/Kälte, wie
 *     Verteilerplanung) ---------------------------------------------------
 *
 * Dieselben Klassen wie überall im Gewerk (`baum`, `baum-ebene`, `baum-zeile`,
 * `karte`, `kennzahlen`) — keine neuen Maße und keine neuen Farben.
 */
export function ElektroItVerteiler() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [schraenke, setSchraenke] = useState<ItSchrank[] | null>(null);
  const [gewaehlt, setGewaehlt] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [offen, setOffen] = useState<Set<string>>(new Set());
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!projekt) return;

    (async () => {
      try {
        const a = await api.elektroItSchraenke(projekt.id);
        setSchraenke(a.schraenke ?? []);
        setFehler(null);

        // Alle Geschosse aufgeklappt: Bei 26 Schränken ist Zuklappen keine
        // Hilfe, sondern ein zweiter Klick vor jedem ersten.
        setOffen(new Set((a.schraenke ?? []).map((s) => s.geschoss ?? 'ohne Geschoss')));
      } catch (e) {
        setFehler(e instanceof Error ? e.message : String(e));
      }
    })();
  }, [projekt?.id]);

  const gruppen = useMemo(() => {
    if (!schraenke) return null;

    const s = suche.trim().toLowerCase();

    const treffer = s === ''
      ? schraenke
      : schraenke.filter((x) => x.kennzeichen.toLowerCase().includes(s)
        || (x.bezeichnung ?? '').toLowerCase().includes(s)
        || (x.raumnummer ?? '').toLowerCase().includes(s));

    const karte = new Map<string, ItSchrank[]>();

    for (const x of treffer) {
      const g = x.geschoss ?? 'ohne Geschoss';
      if (!karte.has(g)) karte.set(g, []);
      karte.get(g)!.push(x);
    }

    return [...karte.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([geschoss, kinder]) => ({
        geschoss,
        kinder: kinder.sort((a, b) => a.kennzeichen.localeCompare(b.kennzeichen)),
      }));
  }, [schraenke, suche]);

  const ueberlauf = (schraenke ?? []).filter((s) => s.ueberschritten);

  const kopf = (
    <PageHeader
      titel="Elektro — IT-Verteiler"
      beschreibung={'LAN-Verteiler mit Netzwerkdosen: 19"-Schrank, Bestückung je '
        + 'Höheneinheit und die Netzabgänge nach Art. Keine Unterverteilung — '
        + 'die Stromverteilung steht unter „Verteiler und Leistungsbilanz".'}
      meta={schraenke ? `${schraenke.length} IT-Verteiler` : undefined}
    />
  );

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die IT-Schränke konnten nicht geladen werden."
          technisch={fehler}
        />
      </>
    );
  }

  if (!projekt || !schraenke) {
    return (
      <>
        {kopf}
        <LoadingState text="IT-Schränke werden gerechnet …" zeilen={8} spalten={3} />
      </>
    );
  }

  if (schraenke.length === 0) {
    return (
      <>
        {kopf}
        <EmptyState
          titel="Kein IT-Verteiler mit zugeordneten Anschlüssen."
          erklaerung={'Ein IT-Verteiler trägt Art „it" und bekommt seine Bauteile '
            + 'über den Verteilerbereich des Raumbuchs. Ohne einen einzigen '
            + 'zugeordneten Anschluss gibt es keine Bestückung und keinen '
            + 'Schrankaufriss.'}
        />
      </>
    );
  }

  const gewaehlterSchrank = schraenke.find((s) => s.verteilerId === gewaehlt) ?? null;

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard titel="IT-Verteiler" wert={schraenke.length} />
        <KpiCard titel="Anschlüsse"
                 wert={schraenke.reduce((n, s) => n + s.anschluesseGesamt, 0)} />
        <KpiCard titel="Ports"
                 wert={schraenke.reduce((n, s) => n + s.portsGesamt, 0)} />
        <KpiCard titel="Patchfelder"
                 wert={schraenke.reduce((n, s) => n + s.patchfelder, 0)} />
        <KpiCard titel="Switches"
                 wert={schraenke.reduce((n, s) => n + s.switches, 0)} />
        <KpiCard
          titel="PoE-Bedarf"
          wert={Math.round(schraenke.reduce((n, s) => n + s.poeBedarfW, 0))}
          einheit="W"
          fussnote="aus it_poe_watt je Verwendung — kein Messwert"
        />
        <KpiCard
          titel="über 42 HE"
          wert={ueberlauf.length}
          zustand={ueberlauf.length > 0 ? 'warnung' : 'gut'}
          fussnote={ueberlauf.length > 0
            ? ueberlauf.map((s) => `${s.kennzeichen} ${s.heHoechste} HE`).join(', ')
            : 'jeder Schrank passt in seine Höheneinheiten'}
        />
      </KpiReihe>

      {ueberlauf.length > 0 && (
        <div className="hinweis" style={{ marginTop: 10 }}>
          <IconWarnung size={14} />
          <div style={{ fontSize: 12 }}>
            {ueberlauf.map((s) => (
              <div key={s.verteilerId}>
                <strong>{s.kennzeichen}</strong> braucht {s.heHoechste} HE und hat{' '}
                {s.heJeSchrank} — {s.heHoechste - s.heJeSchrank} HE liegen über der
                Schrankgrenze. Das ist ein Befund, keine Rundung: Entweder ein
                zweiter Schrank oder eine andere Schrankgröße
                (Vorgabe <code>it_he_je_schrank</code>, unbestätigt).
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="karte" style={{ display: 'flex', gap: 0, padding: 0 }}>
        <div className="baum">
          <div style={{ padding: '10px 12px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <IconSuche size={14} />
              <input
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                placeholder="Kennzeichen, Bezeichnung oder Raum"
                style={{ width: '100%', padding: '4px 6px', fontSize: 13 }}
              />
            </label>
          </div>

          {(gruppen ?? []).map((g) => (
            <div key={g.geschoss}>
              <button
                className="baum-ebene"
                onClick={() => setOffen((s) => {
                  const neu = new Set(s);
                  if (neu.has(g.geschoss)) neu.delete(g.geschoss);
                  else neu.add(g.geschoss);
                  return neu;
                })}
              >
                <IconElektro size={14} />
                <span>{g.geschoss}</span>
                <span className="schwach">{g.kinder.length}</span>
              </button>

              {(offen.has(g.geschoss) || suche.trim() !== '') && g.kinder.map((s) => (
                <button
                  key={s.verteilerId}
                  className={`baum-zeile ${gewaehlt === s.verteilerId ? 'aktiv' : ''}`}
                  onClick={() => setGewaehlt(s.verteilerId)}
                  title={`${s.anschluesseGesamt} Anschlüsse, ${s.heHoechste} von `
                       + `${s.heJeSchrank} HE`}
                >
                  <span style={{ minWidth: 0, overflow: 'hidden',
                                 textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.kennzeichen}
                    <span className="schwach" style={{ marginLeft: 5 }}>
                      {s.anschluesseGesamt} Ansch.
                    </span>
                  </span>

                  {s.ueberschritten && (
                    <StatusBadge zustand="warnung" text={`${s.heHoechste} HE`} klein />
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>

        <div style={{ flex: 1, padding: '14px 16px', minWidth: 0,
                      maxHeight: '80vh', overflowY: 'auto' }}>
          {!gewaehlterSchrank ? (
            <EmptyState
              klein
              titel="Noch kein IT-Verteiler gewählt."
              erklaerung={'Links ein Geschoss aufklappen und einen Schrank wählen — '
                + 'dann stehen hier der Schrankaufriss, die Bestückung je '
                + 'Höheneinheit und die Netzabgänge nach Art.'}
            />
          ) : (
            <Schrankinhalt api={api} schrank={gewaehlterSchrank} />
          )}
        </div>
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Der gewählte Schrank
// ═══════════════════════════════════════════════════════════════════════════

export function Schrankinhalt({ api, schrank }: { api: ApiClient; schrank: ItSchrank }) {
  const [bild, setBild] = useState<string | null>(null);
  const [bildFehler, setBildFehler] = useState<string | null>(null);
  const [laedtPdf, setLaedtPdf] = useState(false);
  const [laedtAnschlussPdf, setLaedtAnschlussPdf] = useState(false);

  useEffect(() => {
    let alt: string | null = null;
    setBild(null);
    setBildFehler(null);

    (async () => {
      try {
        const url = await api.elektroItSchrankblattSvg(schrank.verteilerId);
        alt = url;
        setBild(url);
      } catch (e) {
        setBildFehler(e instanceof Error ? e.message : String(e));
      }
    })();

    // Die Objekt-URL wieder freigeben - sonst hält jeder Schrankwechsel ein
    // weiteres Blatt im Speicher.
    return () => { if (alt) URL.revokeObjectURL(alt); };
  }, [schrank.verteilerId]);

  const abgangsspalten: Spalte<ItAbgang & { _nr: number }>[] = [
    {
      schluessel: 'bezeichnung', titel: 'Art', immerSichtbar: true,
      wert: (a) => a.bezeichnung,
    },
    { schluessel: 'kostengruppe', titel: 'KG', wert: (a) => a.kostengruppe },
    { schluessel: 'stueck', titel: 'Stück', art: 'zahl', wert: (a) => a.stueck },
    {
      schluessel: 'portsJeStueck', titel: 'Ports je Stück', art: 'zahl',
      wert: (a) => a.portsJeStueck,
    },
    { schluessel: 'ports', titel: 'Ports', art: 'zahl', wert: (a) => a.ports },
    {
      schluessel: 'poeKlasse', titel: 'PoE-Klasse',
      wert: (a) => (a.poeBedarfW > 0 ? a.poeKlasse ?? 'nicht hinterlegt' : 'kein PoE'),
    },
    {
      schluessel: 'poe', titel: 'PoE-Bedarf', art: 'zahl', einheit: 'W', nachkomma: 1,
      wert: (a) => (a.poeBedarfW > 0 ? a.poeBedarfW : null),
    },
    {
      schluessel: 'leitung', titel: 'Leitung',
      wert: (a) => a.leitungsart ?? 'nicht hinterlegt',
    },
    {
      schluessel: 'patchfeld', titel: 'Patchfeld',
      wert: (a) => (a.patchfeldVon === a.patchfeldBis
        ? a.patchfeldVon : `${a.patchfeldVon}–${a.patchfeldBis}`),
    },
    {
      schluessel: 'portnummern', titel: 'Portnummern',
      wert: (a) => (a.ports > 0 ? `${a.portVon}–${a.portBis}` : '—'),
    },
  ];

  const bestueckungsspalten: Spalte<ItSchrank['bestueckung'][number] & { _nr: number }>[] = [
    {
      schluessel: 'he', titel: 'HE', immerSichtbar: true,
      wert: (z) => (z.vonHe === z.bisHe ? `${z.vonHe}` : `${z.vonHe}–${z.bisHe}`),
      inhalt: (z) => (z.ausserhalb || z.ragtUeber
        ? <StatusBadge zustand="warnung"
                       text={z.vonHe === z.bisHe ? `${z.vonHe}` : `${z.vonHe}–${z.bisHe}`}
                       klein />
        : <>{z.vonHe === z.bisHe ? z.vonHe : `${z.vonHe}–${z.bisHe}`}</>),
    },
    { schluessel: 'kennzeichen', titel: 'Kennzeichen', wert: (z) => z.kennzeichen },
    { schluessel: 'art', titel: 'Art', wert: (z) => z.art },
    { schluessel: 'bezeichnung', titel: 'Bezeichnung', wert: (z) => z.bezeichnung },
    {
      schluessel: 'ports', titel: 'Ports belegt',
      wert: (z) => (z.ports == null ? '—' : `${z.portsBelegt ?? 0}/${z.ports}`),
    },
    {
      schluessel: 'poe', titel: 'PoE',
      wert: (z) => (z.poeBudgetW == null ? '—'
        : `${(z.poeBedarfW ?? 0).toLocaleString('de-DE')} / `
          + `${z.poeBudgetW.toLocaleString('de-DE')} W`),
    },
  ];

  const daten = schrank.abgaenge.map((a, i) => ({ ...a, _nr: i }));
  const einbau = schrank.bestueckung.map((z, i) => ({ ...z, _nr: i }));

  const gruppeName: Record<string, string> = {
    daten: 'Datennetz (KG 4571) — Arbeitsplatz und WLAN',
    sicherheit: 'Sicherheitstechnik (KG 4543/4567) — Kamera und Zutritt',
    unbekannt: 'Ohne Kostengruppe im Katalog',
  };

  return (
    <>
      <h2 className="karte-titel" style={{ marginTop: 0 }}>
        <IconElektro />{schrank.kennzeichen}
        {schrank.bezeichnung && (
          <span className="schwach">{schrank.bezeichnung}</span>
        )}
        {schrank.raumnummer && (
          <span className="schwach">Raum {schrank.raumnummer}</span>
        )}
      </h2>

      <p className="schwach" style={{ fontSize: 12, maxWidth: 820 }}>
        Höheneinheiten von {schrank.zaehlrichtung === 'unten' ? 'unten' : 'oben'}{' '}
        gezählt (Vorgabe <code>it_he_zaehlrichtung</code>) — HE 1 ist die{' '}
        {schrank.zaehlrichtung === 'unten' ? 'unterste' : 'oberste'} Ebene.
        {' '}Bestückung: {schrank.herkunft}.
      </p>

      <KpiReihe>
        <KpiCard titel="Anschlüsse" wert={schrank.anschluesseGesamt} />
        <KpiCard titel="Ports" wert={schrank.portsGesamt} />
        <KpiCard titel="Patchfelder" wert={schrank.patchfelder} />
        <KpiCard
          titel="Switches"
          wert={schrank.switches}
          fussnote={`${schrank.switchesAusPorts} aus Ports, `
            + `${schrank.switchesAusPoe} aus PoE-Budget`}
        />
        <KpiCard
          titel="PoE-Bedarf"
          wert={schrank.poeBedarfW}
          einheit="W"
          nachkomma={1}
          fussnote={`von ${schrank.poeBudgetW.toLocaleString('de-DE')} W Budget`}
        />
        <KpiCard
          titel="Höheneinheiten"
          wert={schrank.heHoechste}
          einheit={`von ${schrank.heJeSchrank}`}
          zustand={schrank.ueberschritten ? 'warnung' : 'gut'}
          fussnote={`${schrank.hoeheneinheitenBelegt} HE Bestückung + `
            + `${schrank.hoeheneinheitenReserve} HE Reserve`}
        />
        <KpiCard
          titel="Bemessungsstrom"
          wert={schrank.bemessungsstromA}
          einheit="A"
          fussnote={`${schrank.bedarfsleistungW.toLocaleString('de-DE')} W Bedarf`}
        />
      </KpiReihe>

      <ContentSection
        titel="Schrankaufriss"
        beschreibung={'19"-Raster mit ' + schrank.heJeSchrank + ' HE: Patchfeld, '
          + 'Rangierfeld, Switch, PDU, USV und Reserve auf ihrem Platz — '
          + 'dieselbe Zeichnung wie auf dem Blatt ELT-IT.'}
        aktionen={(
          <>
            <button
              onClick={async () => {
                setLaedtPdf(true);
                try {
                  await api.elektroItSchrankblattPdf(
                    schrank.verteilerId, schrank.kennzeichen);
                } catch (e) {
                  setBildFehler(e instanceof Error ? e.message : String(e));
                } finally {
                  setLaedtPdf(false);
                }
              }}
              disabled={laedtPdf}
              style={{ fontSize: 12 }}
            >
              <IconHerunterladen size={13} /> Blatt als PDF
            </button>

            {/* Nur mit Anschluss - ohne eine einzige zugeordnete Komponente
                liefert der Endpunkt sicher 404, und den anzubieten hieße,
                einen Fehler zum Anklicken auszulegen (dasselbe Muster wie
                ELT-VTP an einem leeren Verteiler). */}
            {schrank.anschluesseGesamt > 0 && (
              <button
                onClick={async () => {
                  setLaedtAnschlussPdf(true);
                  try {
                    await api.elektroItAnschlussschemaPdf(
                      schrank.verteilerId, schrank.kennzeichen);
                  } catch (e) {
                    setBildFehler(e instanceof Error ? e.message : String(e));
                  } finally {
                    setLaedtAnschlussPdf(false);
                  }
                }}
                disabled={laedtAnschlussPdf}
                style={{ fontSize: 12 }}
              >
                <IconHerunterladen size={13} /> Anschlussschema (ELT-IT)
              </button>
            )}
          </>
        )}
      >
        {bildFehler ? (
          <ErrorState klein meldung="Der Schrankaufriss ließ sich nicht zeichnen."
                      technisch={bildFehler} />
        ) : !bild ? (
          <LoadingState text="Schrankaufriss wird gezeichnet …" />
        ) : (
          <img
            src={bild}
            alt={`Schrankaufbau ${schrank.kennzeichen}`}
            style={{ width: '100%', maxWidth: 1200, border: '1px solid var(--linie)' }}
          />
        )}
      </ContentSection>

      <ContentSection
        titel={`Netzabgänge nach Art (${schrank.anschluesseGesamt})`}
        beschreibung={'Getrennt nach Kostengruppe: Datennetz (4571) gegen '
          + 'Sicherheitstechnik (4543/4567). Beide liegen heute auf denselben '
          + 'Switches — ob die Sicherheitstechnik ein eigenes Netz bekommt, ist '
          + 'eine offene Frage an den Fachplaner und hier bewusst nicht '
          + 'entschieden.'}
      >
        {['daten', 'sicherheit', 'unbekannt']
          .filter((g) => daten.some((a) => a.anlagengruppe === g))
          .map((g) => (
            <div key={g} style={{ marginBottom: 12 }}>
              <div className="feldname" style={{ marginBottom: 4 }}>
                {gruppeName[g] ?? g}
                <span className="sehr-schwach" style={{ fontWeight: 400, marginLeft: 8 }}>
                  {daten.filter((a) => a.anlagengruppe === g)
                        .reduce((n, a) => n + a.ports, 0)} Ports ·{' '}
                  {daten.filter((a) => a.anlagengruppe === g)
                        .reduce((n, a) => n + a.poeBedarfW, 0)
                        .toLocaleString('de-DE', { maximumFractionDigits: 1 })} W PoE
                </span>
              </div>

              <DataTable
                zeilen={daten.filter((a) => a.anlagengruppe === g)}
                spalten={abgangsspalten}
                schluessel={(a) => `${a._nr}`}
                dicht
              />
            </div>
          ))}

        <p className="sehr-schwach" style={{ fontSize: 11, maxWidth: 820 }}>
          Das Patchfeld ist die feste Verkabelung; der Switchport entsteht beim
          Rangieren. Die PoE-Last wird dabei so über die Switches verteilt, dass
          keiner über sein Budget läuft — deshalb steht hier kein Switchport.
        </p>
      </ContentSection>

      <ContentSection
        titel={`Bestückung (${schrank.bestueckung.length} Betriebsmittel)`}
        beschreibung="Jede Zeile mit ihrem Platz im 19&quot;-Raster."
      >
        <DataTable
          zeilen={einbau}
          spalten={bestueckungsspalten}
          schluessel={(z) => `${z._nr}`}
          dicht
        />

        {schrank.inDerFlanke.length > 0 && (
          <p className="sehr-schwach" style={{ fontSize: 11 }}>
            In der Schrankflanke (0 HE im 19"-Raster):{' '}
            {schrank.inDerFlanke.map((z) => `${z.kennzeichen} ${z.bezeichnung ?? ''}`)
              .join(', ')}
          </p>
        )}
      </ContentSection>

      {schrank.hinweise.length > 0 && (
        <ContentSection titel={`Hinweise (${schrank.hinweise.length})`}>
          <ul style={{ fontSize: 12, margin: 0, paddingLeft: 18 }}>
            {schrank.hinweise.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </ContentSection>
      )}

      <ContentSection titel="Bilanzkette">
        <code style={{ fontSize: 11, whiteSpace: 'pre-wrap' }}>
          {schrank.bilanzKette}
        </code>
      </ContentSection>
    </>
  );
}
